import React, { useEffect, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import nextId from 'react-id-generator'
import { useSelector } from 'react-redux'

import { Card, SectionHeader } from '@/components/Elements'

import DownloadButton from '@/components/Elements/Button/DownloadButton'
import { Checkbox } from '@/components/Form'
import { Input, VersionFileInput } from '@/components/Form/'
import {
  // [CR] Issue #5 — re-enable when Damex YY banner CR is approved
  // Alert,
  // AlertIcon,
  // AlertTitle,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  InputGroup,
  Text,
  VStack,
} from '@chakra-ui/react'

//Constant

import useConfig from '@/features/configs/useConfig'
import useFile from '@/features/files/useFile'
import { OfferDocumentTypes } from '@/features/offers/constants'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import SharepointFileList from '@/components/Form/SharepointFileList'
import { isNullish } from '@/utils/helper'
import { readSpecificCellsFromExcel } from '@/utils/fileHelper'

const DocumentForm = () => {
  // Redux
  const offerPermissions = useSelector(selectOfferPermissions)

  const [requiredDocuments, setRequiredDocuments] = useState([])
  const { useFetchConfigByType } = useConfig()
  const { useFetchFile } = useFile()

  const { data: submitCheckDocumentConfig } = useFetchConfigByType('SUBMITTED_OFFER_CHECKING')
  const { data: offerCostSheetTemplateConfig } = useFetchConfigByType('OFFER_COST_SHEET_TEMPLATE')

  const { data: offerCostSheetTemplateFile } = useFetchFile(offerCostSheetTemplateConfig?.mainValue ?? '')

  const {
    formState: { errors },
    getValues,
    setValue,
    register,
  } = useFormContext()

  const subSystems = useWatch({ name: 'subSystems' })
  const isRequiredExportControl = useWatch({ name: 'isRequiredExportControl' })
  const isRequiredSalesConfirmDamex = useWatch({ name: 'isRequiredSalesConfirmDamex' })
  const isSiemensStandardTerms = useWatch({
    name: 'commercialChecklist.isSiemensStandardTerms',
  })
  const confirmDamexTime = useWatch({ name: 'confirmDamexTime' })
  const countryOfInstallation = useWatch({ name: 'countryOfInstallation' })

  const {
    fields: internalOtherFields,
    append: addInternalOther,
    remove: removeInternalOther,
  } = useFieldArray({ name: 'documents.internalOthers' })
  const {
    fields: externalOtherFields,
    append: addExternalOther,
    remove: removeExternalOther,
  } = useFieldArray({ name: 'documents.externalOthers' })
  const {
    fields: tenderQueryFields,
    append: addTenderQuery,
    remove: removeTenderQuery,
  } = useFieldArray({ name: 'documents.tenderQueries' })

  //Translated
  const canEditDocument = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_DOCUMENT]?.edit ?? false
  const canEditDocumentLegal = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_DOCUMENTS_LEGAL]?.edit ?? false
  const canEditDocumentSignedProposal =
    offerPermissions[offerQuotationGroupPolicyType.QUOTATION_DOCUMENTS_SIGNED_COMMERCIAL_PROPOSAL]?.edit ?? false
  const canEditDocumentTenderQuery =
    offerPermissions[offerQuotationGroupPolicyType.QUOTATION_DOCUMENT_TENDER_QUERY]?.edit ?? false
  const canConfirmDamex =
    (offerPermissions[offerQuotationGroupPolicyType.QUOTATION_DOCUMENT_CONFIRM_DAMEX]?.edit ?? false) &&
    isRequiredSalesConfirmDamex

  useEffect(() => {
    if (submitCheckDocumentConfig?.value && Array.isArray(submitCheckDocumentConfig.value)) {
      setRequiredDocuments(submitCheckDocumentConfig.value)
    }
  }, [submitCheckDocumentConfig])

  return (
    <Grid templateColumns="repeat(2, minmax(0, 1fr))" templateRows="repeat(3, 1fr)" gap={6} overflowX="hidden">
      <GridItem rowSpan="1">
        <Card
          title="External Proposal"
          rightElement={
            canEditDocument && (
              <Button
                colorScheme="green"
                onClick={() => {
                  addExternalOther({ name: '', files: [] })
                }}
              >
                Add
              </Button>
            )
          }
        >
          <VStack spacing="15px">
            <VersionFileInput
              label="1.1a) Commercial Proposal (e.g. Cover letter,FOT, SOR)"
              name="documents.externalCommercialProposal"
              fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_PROPOSAL}
              canUpload={canEditDocument}
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_PROPOSAL)}
            />
            <VersionFileInput
              label="1.1b) Commercial deviation"
              name="documents.externalCommercialDeviation"
              fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_DEVIATION}
              canUpload={canEditDocument}
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_DEVIATION)}
            />
            <VersionFileInput
              label="1.1c) Cover letter"
              name="documents.externalCoverLetter"
              fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_COVER_LETTER}
              canUpload={canEditDocument}
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_EXTERNAL_COVER_LETTER)}
            />
            <VersionFileInput
              label="1.2 Technical Proposal"
              name="documents.externalTechnicalProposal"
              fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_TECHNICAL_PROPOSAL}
              canUpload={canEditDocument}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_EXTERNAL_TECHNICAL_PROPOSAL)}
            />
            <VersionFileInput
              label="1.3 Signed Commercial Proposal"
              name="documents.signedCommercialProposal"
              fileType={OfferDocumentTypes.DOCUMENTS_SIGNED_COMMERCIAL_PROPOSAL}
              canUpload={canEditDocumentSignedProposal}
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_SIGNED_COMMERCIAL_PROPOSAL)}
            />
            <VersionFileInput
              label="1.4 NDA"
              name="documents.externalNDA"
              fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_PROPOSAL}
              canUpload={canEditDocument}
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_EXTERNAL_NDA)}
            />
            {externalOtherFields.map((other, index) => (
              <FormControl isInvalid={errors} as={Flex} key={'externalOthers_' + index}>
                <HStack w="40%" mr="12px">
                  <FormLabel>1.{index + 5}</FormLabel>
                  <Input name={`documents.externalOthers.${index}.name`} type="text" disabled={!canEditDocument} />
                  {canEditDocument && <CloseButton onClick={() => removeExternalOther(index)} />}
                </HStack>
                <InputGroup>
                  <VersionFileInput
                    name={`documents.externalOthers.${index}.files`}
                    fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_OTHERS}
                    canUpload={canEditDocument}
                    isRow
                    labelWidth={40}
                  />
                </InputGroup>
              </FormControl>
            ))}
          </VStack>
        </Card>
      </GridItem>
      <GridItem rowSpan="1">
        <Card title="Tender Documents / Customer Enquiry or RFQ">
          <SharepointFileList
            type="tenderFiles"
            name="documents.tenderFile"
            isRow
            labelWidth={40}
            canUpload={canEditDocumentTenderQuery}
          />

          <SectionHeader
            title="Tender Query / Customer's follow-up Doc"
            rightElement={
              canEditDocument ? (
                <Button
                  disabled={!canEditDocument}
                  colorScheme="green"
                  onClick={() => {
                    addTenderQuery({ name: '', files: [] })
                  }}
                >
                  Add
                </Button>
              ) : (
                // <Button
                //   disabled={!canEdit}
                //   colorScheme="green"
                //   onClick={() => {
                //     addTenderQuery({ name: '', files: [] })
                //   }}
                // >
                //   Add
                // </Button>
                <></>
              )
            }
          ></SectionHeader>

          <Flex gap={2} direction={'column'} mt="2">
            {/* {tenderQueryFields.map((other, index) => (
              <FormControl isInvalid={errors} as={Flex} key={nextId('tenderQuery_')}>
                <HStack w="40%" mr="12px">
                  <Input
                    name={`documents.tenderQueries.${index}.name`}
                    type="text"
                    disabled={!canEditDocumentTenderQuery}
                  />
                  {canEditDocumentTenderQuery && <CloseButton onClick={() => removeTenderQuery(index)} />}
                </HStack>
                <InputGroup w="60%">
                  <VersionFileInput
                    name={`documents.tenderQueries.${index}.files`}
                    fileType={OfferDocumentTypes.DOCUMENTS_TENDER_QUERIES}
                    canUpload={canEditDocumentTenderQuery}
                    canDelete
                    isRow
                    isMultiple
                    labelWidth={40}
                  />
                </InputGroup>
              </FormControl>
            ))} */}
            <SharepointFileList
              type="tenderQueries"
              name="documents.tenderFile"
              isRow
              labelWidth={40}
              canUpload={canEditDocumentTenderQuery}
            />
          </Flex>
        </Card>
      </GridItem>

      <GridItem colSpan={2} rowSpan="2">
        <Card
          title="Internal Supporting Documents"
          rightElement={
            canEditDocument && (
              <Button
                colorScheme="green"
                onClick={() => {
                  addInternalOther({ name: '', files: [] })
                }}
              >
                Add
              </Button>
            )
          }
        >
          <VStack spacing="15px" alignItems="flex-start">
            <Flex direction={'column'} w="100%" gap={1}>
              <Flex>
                <Text fontWeight={'medium'} fontSize="md" mb="3" w="40%">
                  2.1 Cost sheet (.xlsm Only)
                </Text>
                <DownloadButton
                  fileId={offerCostSheetTemplateFile?.data?._id}
                  fileName={offerCostSheetTemplateFile?.data?.originalFileName}
                  displayText={'Download Quotation Template '}
                />
              </Flex>

              {subSystems &&
                subSystems.map((subSystem, index) => (
                  <VersionFileInput
                    key={index}
                    label={`Subsystem ${index + 1}: ${subSystem.projectName}`}
                    name={`subSystems.${index}.internalCostSheet`}
                    fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_COSTSHEET}
                    canUpload={canEditDocument}
                    isRow
                    labelWidth={38.5}
                    isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_COSTSHEET)}
                    allowedFileTypes={'.xlsm, .xlsx'}
                    ml="5"
                    onFilesSelected={async (files) => {
                      if (files && files.length > 0) {
                        try {
                          const cells = await readSpecificCellsFromExcel(files[0], 'Quotation BQ (Core)', [
                            'S675',
                            'G612',
                          ])

                          let parsedNum = null
                          if (cells['S675'] !== null && cells['S675'] !== '' && !isNaN(Number(cells['S675']))) {
                            parsedNum = Number(cells['S675'])
                          } else if (cells['G612'] !== null && cells['G612'] !== '' && !isNaN(Number(cells['G612']))) {
                            parsedNum = Number(cells['G612'])
                          }

                          if (parsedNum !== null) {
                            setValue(`subSystems.${index}.costSheetContractValue`, parsedNum, { shouldDirty: true })
                            if (countryOfInstallation === 'hk') {
                              if (parsedNum >= 5000000) {
                                setValue('SOPQuestionAnswers.isOver5Million', true, { shouldDirty: true })
                              } else {
                                setValue('SOPQuestionAnswers.isOver5Million', false, { shouldDirty: true })
                              }
                            }
                          }
                        } catch (e) {
                          console.error('Error parsing cost sheet for contract value', e)
                        }
                      }
                    }}
                  />
                ))}
            </Flex>

            <VersionFileInput
              label="2.2 Damex-E (Link to Damax-E page here)"
              name="documents.internalDamexE"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_DAMEXE}
              canUpload={canEditDocument}
              link="https://damex.siemens.com/"
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_DAMEXE)}
            />
            {/* [CR] Issue #5 — Damex YY banner deferred as Change Request per Zocialab (30/6/2026)
            {isRequiredExportControl && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertTitle>Damex AWV Status: YY — Export Control Required</AlertTitle>
              </Alert>
            )}
            */}
            {isRequiredExportControl && (
              <VersionFileInput
                label="2.2.1 ECC Supporting Document"
                name="documents.internalECCSupporting"
                fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_ECC_SUPPORTING}
                canUpload={canEditDocument}
                isMultiple
                isRow
                labelWidth={40}
                isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_ECC_SUPPORTING)}
              />
            )}

            {canConfirmDamex && (
              <Flex>
                <Checkbox disabled={!isNullish(confirmDamexTime)} name="isDamexConfirmedBySales" />
                <Text>I confirm I had reviewed and confirm the information in Damex E is correct</Text>
              </Flex>
            )}

            <VersionFileInput
              label="2.3 PSS (Link to PSS portal here)"
              name="documents.internalPSS"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_PSS}
              canUpload={canEditDocument}
              link="https://siemens.sharepoint.com/teams/p0008138/PSS/Lists/Project%20Classification/From_July_2020.aspx"
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_PSS)}
            />

            <VersionFileInput
              label="2.4 OCC"
              name="documents.internalOCC"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_OCC}
              canUpload={canEditDocument}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_OCC)}
            />

            <VersionFileInput
              label="2.5 LoA & PRR"
              name="documents.internalLoA"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_LOA}
              canUpload={canEditDocument}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_LOA)}
            />

            <VersionFileInput
              label="2.6 Contributors' Comments"
              name="documents.internalContributorComments"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_CONTRIBUTOR_COMMENTS}
              canUpload={canEditDocument}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_CONTRIBUTOR_COMMENTS)}
            />

            <VersionFileInput
              label="2.7 Insurance Comments"
              name="documents.internalInsuranceComments"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_INSURANCE_COMMENTS}
              canUpload={canEditDocument}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_INSURANCE_COMMENTS)}
            />

            <VersionFileInput
              label="2.8 Bond Comments"
              name="documents.internalBondComments"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_BOND_COMMENTS}
              canUpload={canEditDocument}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_BOND_COMMENTS)}
            />

            <VersionFileInput
              label="2.9 Legal Statement"
              name="documents.internalLegalStatement"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_STATEMENT}
              canUpload={canEditDocumentLegal}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={
                requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_STATEMENT) ||
                isSiemensStandardTerms === 'noWithLegalWaiver'
              }
            />

            <VersionFileInput
              label="2.10 Legal comments"
              name="documents.internalLegalComments"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_COMMENTS}
              canUpload={canEditDocumentLegal}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_COMMENTS)}
            />

            <VersionFileInput
              label="2.11 IPR Checklist"
              name="documents.internalIPRChecklist"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_IPR_CHECKLIST}
              canUpload={canEditDocumentLegal}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_IPR_CHECKLIST)}
            />
            <VersionFileInput
              label="2.12 TRR Report"
              name="documents.internalTRRReport"
              fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_TRR_REPORT}
              canUpload={canEditDocument}
              isMultiple
              isRow
              labelWidth={40}
              isRequired={requiredDocuments.includes(OfferDocumentTypes.DOCUMENTS_INTERNAL_TRR_REPORT)}
            />

            {internalOtherFields.map((other, index) => (
              <FormControl isInvalid={errors} as={Flex} key={nextId('internalOthers_')}>
                <HStack w="40%" mr="12px">
                  <FormLabel>2.{index + 13}</FormLabel>
                  <Input name={`documents.internalOthers.${index}.name`} type="text" disabled={!canEditDocument} />
                  {canEditDocument && <CloseButton onClick={() => removeInternalOther(index)} />}
                </HStack>
                <InputGroup>
                  <VersionFileInput
                    name={`documents.internalOthers.${index}.files`}
                    fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_OTHERS}
                    canUpload={canEditDocument}
                    isRow
                    labelWidth={40}
                  />
                </InputGroup>
              </FormControl>
            ))}
          </VStack>
        </Card>
      </GridItem>
    </Grid>
  )
}

export default DocumentForm
